// ============================================
// WITHDRAWAL HOOKS
// Combines hooks from:
// - [WithdrawalHelpers.tsx] -> [Withdrawal.tsx] Withdrawal (3404-3407)
// - [WithdrawalSources.tsx] -> [WithdrawalHelpers.tsx] -> [Withdrawal.tsx] Withdrawal (300-303)
// - [WithdrawalSourcesHelpers.tsx] -> [WithdrawalSources.tsx] -> Withdrawal (304-314)
// - [NewCurrentAccountDetails.tsx] -> [WithdrawalHelpers.tsx] -> Withdrawal (351-359)
// ============================================

import React, {useEffect} from 'react';
import {FieldInputProps} from 'formik';
import moment, {MomentInput} from 'moment';
import {SelectGroupOption} from '../../../components/AutoForm/AutoFieldNew';
import FREQUENCY, {FREQUENCY_COMBOS} from '../../../constants/Frequency';
import {OVRecommendActionEnum, BankDetailsEnum} from '../../../schema-types/globalTypes';
import OWNERSHIP from '../../../constants/Ownership';
import {moneyFieldValueType} from '../../../components/AutoForm/AutoFormNew';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {withdrawalFieldsType} from '../RecommendsWithdrawal/withdrawalFields';
import {getCurrencyFloat} from '../../../utils/currencyFormatter';
import loadMomentDate from '../../../utils/loadMomentDate';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {getWithdrawalMinimumDate} from '../RecommendsWithdrawal/getWithdrawalMinimumDate';
import {FULL_SURRENDER} from '../RecommendsWithdrawal/withdrawalFormFields';
import {includesSaltus} from '../../../utils/includesSaltus';
import {Pot} from '../../../schema-types/Pot';
import {NEW_PLAN_ID} from '../CreateRecommend/RecommendSourceDestination/constants';
import {UPDATES} from '../../../shared/UPDATES';
import {
  useAutoForm,
  useAutoField,
  AutoFieldHelpers,
  SetFieldValueWithSourceKey,
} from '../../../components/AutoForm/useAutoForm';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';

// ============================================
// WithdrawalHelpers.tsx (3404-3407)
// ============================================

export const changeWithdrawalSourceFactory = ({
  sourcePots,
  withdraw,
  withdrawSourceHelpers,
}: {
  sourcePots: Pot[];
  withdraw: withdrawalFieldsType | undefined;
  withdrawSourceHelpers: AutoFieldHelpers<withdrawalFieldsType['source']>;
}) => {
  return (event: React.ChangeEvent<any> | null, value: string) => {
    const srcId = value || event?.target?.value;
    const srcObj = sourcePots.find((x) => x.id === srcId);
    withdrawSourceHelpers.setValue(UPDATES._3404, {
      singleClientAmount: 0,
      frequency: FREQUENCY.SINGLE,
      ...withdraw?.source,
      id: srcId,
      maxAmount: srcObj?.totalValue || 0,
      isSaltus: includesSaltus(srcObj?.productProvider?.formattedName),
      crystallisedFunds: srcObj?.crystallisedFunds,
      uncrystallisedFunds: srcObj?.uncrystallisedFunds,
    });
  };
};

export const changeWithdrawalDestinationFactory = ({
  withdraw,
  withdrawDestinationHelpers,
}: {
  withdraw: withdrawalFieldsType | undefined;
  withdrawDestinationHelpers: AutoFieldHelpers<withdrawalFieldsType['destination']>;
}) => {
  return (event: React.ChangeEvent<any> | null, value: string) => {
    const dest = value || event?.target?.value;
    withdrawDestinationHelpers.setValue(UPDATES._3405, {
      ...withdraw?.destination,
      id: dest,
    });
  };
};

export const loadSavedWithdrawal = ({
  recommendDetails,
  withdrawHelpers,
}: {
  recommendDetails: OVRecommendation | null;
  withdrawHelpers: AutoFieldHelpers<withdrawalFieldsType | undefined>;
}) => {
  if (!recommendDetails) {
    return;
  }

  const savedWithdrawal = getSavedWithdrawalValues(recommendDetails);
  withdrawHelpers.setValue(UPDATES._3407, {
    ...savedWithdrawal,
  });
};

// ============================================
// WithdrawalSources.tsx (300-303)
// ============================================

export const handleWithdrawalFullAndReason = ({
  withdrawalValues,
  values,
  options,
  setFieldValue,
  setFullAmount,
}: {
  withdrawalValues: withdrawalFieldsType;
  values: recommendFieldsType;
  options: SelectGroupOption[];
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
  setFullAmount: () => void;
}) => {
  if (withdrawalValues.source.full) {
    setFieldValue(UPDATES._300, 'withdraw.source.reason', FULL_SURRENDER);
    setFieldValue(UPDATES._301, 'withdraw.source.frequency', FREQUENCY.SINGLE);
    setFullAmount();
    return;
  }
  if (options.length && options[0]) {
    if (!values.withdraw?.source.reason) {
      setFieldValue(UPDATES._303, 'withdraw.source.reason', null);
    }
    const reasonIsInOptions = options.find((x) =>
      x.options.find((y) => y.value === values.withdraw?.source.reason)
    );
    if (!reasonIsInOptions) {
      setFieldValue(UPDATES._303, 'withdraw.source.reason', null);
    }
  }
};

export const useHandleWithdrawalFullAndReason = ({
  options,
  setFullAmount,
}: {
  options: SelectGroupOption[];
  setFullAmount: () => void;
}) => {
  const {values, setFieldValue} = useAutoForm<recommendFieldsType>();
  const withdrawalValues = values.withdraw!;
  useEffect(() => {
    handleWithdrawalFullAndReason({
      withdrawalValues,
      values,
      options,
      setFieldValue,
      setFullAmount,
    });
  }, [options, values.withdraw?.source.reason, withdrawalValues.source.full]);
};

// ============================================
// WithdrawalSourcesHelpers.tsx (304-314)
// ============================================

export const setWithdrawalFullAmountFactory = ({
  withdrawalValues,
  totalAmount,
  setAmount,
}: {
  withdrawalValues: withdrawalFieldsType;
  totalAmount: number | undefined;
  setAmount: AutoFieldHelpers<moneyFieldValueType>['setValue'];
}) => {
  return () => {
    if (withdrawalValues.source?.full) {
      setAmount(UPDATES._306, totalAmount || '');
      withdrawalValues.source.singleClientAmount = totalAmount || '';
    } else {
      setAmount(UPDATES._307, withdrawalValues.source?.singleClientAmount);
    }
  };
};

export const setStartDateMustBe10daysInTheFuture = ({
  withdrawalValues,
  values,
  setFieldValue,
  today, // need to pass this in for testing
  recommendDetails,
}: {
  withdrawalValues: withdrawalFieldsType;
  values: recommendFieldsType;
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
  today: MomentInput;
  recommendDetails: OVRecommendation | null;
}) => {
  if (
    withdrawalValues.source?.start ||
    values.withdraw?.source?.frequency === FREQUENCY.SINGLE
  ) {
    return;
  }
  const currentOrSavedDate =
    withdrawalValues.source?.start || recommendDetails?.startDate;
  const minimumDate = getWithdrawalMinimumDate(today, moment(currentOrSavedDate));

  setFieldValue(
    UPDATES._304,
    'withdraw.source.start',
    // withdrawal start date can only be at least 10 days in the future
    minimumDate,
    true
  );
};

export const getSavedWithdrawalValues = (
  recommendDetails: OVRecommendation | null
): Omit<withdrawalFieldsType, 'planProvider'> => {
  const savedSource = recommendDetails?.pots?.find((p) => p?.planType === 'source');

  const frequency = recommendDetails?.pots?.[0]?.recurringClientFrequency
    ? (recommendDetails.pots[0].recurringClientFrequency as FREQUENCY)
    : FREQUENCY.SINGLE;

  const source: withdrawalFieldsType['source'] = {
    frequency,
    singleClientAmount: getCurrencyFloat(savedSource?.singleClientAmount) || '',
    id: savedSource?.pot?.id,
    reason:
      savedSource?.pot.withdrawalReason ||
      recommendDetails?.withdrawalReason ||
      undefined,
    recurringClientAmount: getCurrencyFloat(savedSource?.recurringClientAmount),
    maxAmount: savedSource?.pot.totalValue,
    full: savedSource?.full || false,
    isSaltus: includesSaltus(savedSource?.pot.productProvider?.formattedName),
    crystallisedFunds: savedSource?.pot.crystallisedFunds,
    uncrystallisedFunds: savedSource?.pot.uncrystallisedFunds,
    start: loadMomentDate(
      recommendDetails?.startDate ? String(recommendDetails?.startDate) : undefined
    ),
  };

  const initialFee = savedSource?.initialFee?.id;

  // only selecting from list if there is saved data
  const savedDestination = recommendDetails?.pots?.find(
    (p) => p?.planType === 'destination'
  );
  // cannot find this plan in destinations? then it's a new one
  const isNewPlan = savedDestination?.pot.productIsBank && !savedDestination.pot.curoId;

  const destination: withdrawalFieldsType['destination'] | undefined = savedDestination
    ? isNewPlan
      ? {
          id: NEW_PLAN_ID,
          newExistingPotId: savedDestination.pot.id,
          sortCode: savedDestination.pot.sortCode || undefined,
          accountNumber: savedDestination.pot.planNumber || undefined,
          productProvider: savedDestination.pot.productProvider?.id,
          accountName: savedDestination.pot.policyHolder || '',
          bankDetails: savedDestination.pot.bankDetails,
          contactId: !savedDestination.pot.contact?.id
            ? OWNERSHIP.JOINT_CODE
            : savedDestination.pot.contact?.id,
        }
      : {id: savedDestination.pot.id}
    : undefined;

  return {source, initialFee, ...(destination ? {destination} : {})};
};

// export const loadSavedWithdrawalSource = ({
//   recommendDetails,
//   setFieldValue,
// }: {
//   recommendDetails: OVRecommendation | null;
//   setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
// }) => {
//   if (!recommendDetails) {
//     setFieldValue(UPDATES._301, 'withdraw.frequency', FREQUENCY.SINGLE, true);
//     return;
//   }
//
//   const frequency = recommendDetails?.pots?.[0]?.recurringClientFrequency
//     ? recommendDetails.pots[0].recurringClientFrequency
//     : FREQUENCY.SINGLE;
//   setFieldValue(UPDATES._308, 'withdraw.frequency', frequency, true);
//   setFieldValue(
//     UPDATES._310,
//     'withdraw.source.start',
//     loadMomentDate(
//       recommendDetails?.startDate ? String(recommendDetails?.startDate) : undefined
//     ),
//     true
//   );
//   const sourcePot = recommendDetails.pots?.find((x) => x.planType === 'source');
//   if (!sourcePot) {
//     return;
//   }
//   setFieldValue(
//     UPDATES._311,
//     'withdraw.source.reason',
//     sourcePot.pot.withdrawalReason || recommendDetails?.withdrawalReason
//   );
//   setFieldValue(
//     UPDATES._312,
//     'withdraw.source.singleClientAmount',
//     getCurrencyFloat(sourcePot?.singleClientAmount)
//   );
//   setFieldValue(
//     UPDATES._313,
//     'withdraw.source.recurringClientAmount',
//     getCurrencyFloat(sourcePot.recurringClientAmount)
//   );
//
//   setFieldValue(UPDATES._314, 'withdraw.initialFee', sourcePot.initialFee?.id, true);
// };

// ============================================
// NewCurrentAccountDetails.tsx (351-359)
// ============================================

export const loadNewCurrentAccount = ({
  recommendDetails,
  setFieldValue,
}: {
  recommendDetails: OVRecommendation | null;
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
}) => {
  if (!recommendDetails) {
    return;
  }
  const destinationPot = recommendDetails?.pots?.find(
    (x) => x?.planType === 'destination'
  )?.pot;

  if (
    destinationPot?.sortCode === '000000' ||
    destinationPot?.planNumber === '00000000' ||
    !destinationPot?.sortCode ||
    !destinationPot?.planNumber
  ) {
    setFieldValue(
      UPDATES._357,
      'withdraw.destination.bankDetails',
      BankDetailsEnum.NOT_KNOWN
    );
  } else {
    setFieldValue(
      UPDATES._356,
      'withdraw.destination.bankDetails',
      BankDetailsEnum.NEED_VALIDATING
    );
  }
  // if (!recommendDetails.contact?.id) {
  //   setFieldValue(
  //     UPDATES._352,
  //     `${PLAN_ACTION.WITHDRAW}.destination.ownership`,
  //     OWNERSHIP.JOINT_CODE
  //   );
  // }
  setFieldValue(
    UPDATES._353,
    'withdraw.destination.productProvider',
    destinationPot?.productProvider?.id
  );
  setFieldValue(
    UPDATES._351,
    'withdraw.destination.accountNumber',
    destinationPot?.planNumber || ''
  );
  setFieldValue(
    UPDATES._354,
    'withdraw.destination.accountName',
    destinationPot?.policyHolder
  );
  setFieldValue(UPDATES._355, 'withdraw.destination.sortCode', destinationPot?.sortCode);
};
